import { SxProps, Theme, AutocompleteProps } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Autocomplete, { AutocompleteAction } from '../Autocomplete';

export type TypeaheadOption<T = unknown> = {
  /** The actual value that will be stored/returned */
  value: T;
  /** The label to display in the dropdown/search */
  label: string;
  /** The parsed display text to show after selection (can use regex patterns) */
  displayText?: string;
  /** Additional data associated with the option */
  data?: Record<string, unknown>;
};

export type TypeaheadFetchFunction<T = unknown> = (searchTerm: string) => Promise<TypeaheadOption<T>[]>;

export type TypeaheadParseFunction<T = unknown> = (option: TypeaheadOption<T>) => string;

export type TypeaheadProps<T = unknown> = {
  /** Label for the input field */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: React.ReactNode;
  /** Whether the input is in an error state */
  error?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input should take up the full width of its container */
  fullWidth?: boolean;
  /** Current value of the input */
  value?: TypeaheadOption<T> | null;
  /** Callback fired when the value changes */
  onChange?: (value: TypeaheadOption<T> | null) => void;
  /** Function to fetch data based on search term */
  fetchData: TypeaheadFetchFunction<T>;
  /** Function to parse the display text after selection (supports regex) */
  parseDisplayText?: TypeaheadParseFunction<T>;
  /** Minimum characters to trigger search */
  minSearchLength?: number;
  /** Whether to show loading state */
  loading?: boolean;
  /** Text to display when loading */
  loadingText?: React.ReactNode;
  /** Text to display when no options are available */
  noOptionsText?: React.ReactNode;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Styles to apply to the component */
  sx?: SxProps<Theme>;
  /** Custom actions to display at the top of the dropdown */
  actions?: AutocompleteAction[];
  /** Whether to focus the input on mount */
  autoFocus?: boolean;
  /** Reference to the input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Placeholder text to display in the input */
  placeholder?: string;
  /** Whether to clear the input on blur */
  clearOnBlur?: boolean;
  /** Whether to clear the input on escape */
  clearOnEscape?: boolean;
  /** Whether to disable the clear button */
  disableClearable?: boolean;
  /** Whether to disable closing on select */
  disableCloseOnSelect?: boolean;
  /** Function to filter options */
  filterOptions?: (options: TypeaheadOption<T>[], state: { inputValue: string }) => TypeaheadOption<T>[];
  /** Function to get the label for an option */
  getOptionLabel?: (option: TypeaheadOption<T>) => string;
  /** Function to check if an option is equal to a value */
  isOptionEqualToValue?: (option: TypeaheadOption<T>, value: TypeaheadOption<T>) => boolean;
  /** Function to render an option */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: TypeaheadOption<T>) => React.ReactNode;
  /** Whether to load initial data on mount */
  loadInitialData?: boolean;
  /** Props to be passed to the internal MUI components */
  slotProps?: AutocompleteProps<TypeaheadOption<T>, false, false, false>['slotProps'];
};

/**
 * Typeahead component that fetches data, allows searching, and shows parsed data after selection
 *
 * @example
 * ```tsx
 * <Typeahead
 *   label="Search Users"
 *   fetchData={async (searchTerm) => {
 *     const response = await fetch(`/api/users?search=${searchTerm}`);
 *     const users = await response.json();
 *     return users.map(user => ({
 *       value: user.id,
 *       label: `${user.firstName} ${user.lastName}`,
 *       displayText: `${user.firstName} ${user.lastName} (${user.email})`,
 *       data: user
 *     }));
 *   }}
 *   parseDisplayText={(option) => {
 *     // Use regex to extract and format the display text
 *     const match = option.displayText?.match(/(.+) \((.+)\)/);
 *     return match ? `${match[1]} - ${match[2]}` : option.label;
 *   }}
 *   onChange={(value) => console.log('Selected:', value)}
 * />
 * ```
 */
const Typeahead = <T = unknown,>({
  label,
  helperText,
  error = false,
  disabled = false,
  fullWidth = true,
  value,
  onChange,
  fetchData,
  parseDisplayText,
  minSearchLength = 2,
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  size = 'medium',
  sx,
  actions,
  autoFocus = false,
  inputRef,
  placeholder,
  clearOnBlur = true,
  clearOnEscape = true,
  disableClearable = false,
  disableCloseOnSelect = false,
  filterOptions,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  loadInitialData = true,
  slotProps
}: TypeaheadProps<T>) => {
  const [options, setOptions] = useState<TypeaheadOption<T>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch data when input changes
  const fetchOptions = useCallback(
    async (searchTerm: string) => {
      // Allow empty search term if loadInitialData is true, otherwise check minSearchLength
      if (!loadInitialData && (!searchTerm || searchTerm.length < minSearchLength)) {
        setOptions([]);
        return;
      }

      try {
        setIsLoading(true);
        setSearchError(null);
        const results = await fetchData(searchTerm);
        setOptions(results);
      } catch (error) {
        console.error('Typeahead fetch error:', error);
        setSearchError('Failed to fetch options');
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData, minSearchLength, loadInitialData]
  );

  // Handle input changes with debounce
  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        fetchOptions(newInputValue);
      }, 300);
    },
    [fetchOptions]
  );

  // Handle option selection
  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: TypeaheadOption<T> | TypeaheadOption<T>[] | null) => {
      // Typeahead only supports single selection
      const singleValue = Array.isArray(newValue) ? newValue[0] || null : newValue;
      onChange?.(singleValue);
    },
    [onChange]
  );

  // Default option label function
  const defaultGetOptionLabel = useCallback((option: TypeaheadOption<T>) => {
    return option.label;
  }, []);

  // Default option equality function
  const defaultIsOptionEqualToValue = useCallback((option: TypeaheadOption<T>, value: TypeaheadOption<T>) => {
    return option.value === value.value;
  }, []);

  // Parse display text for selected value
  const displayValue = useMemo(() => {
    if (!value) return null;

    if (parseDisplayText) {
      return {
        ...value,
        label: parseDisplayText(value)
      };
    }

    return value;
  }, [value, parseDisplayText]);

  // Load initial data if requested
  useEffect(() => {
    if (loadInitialData) {
      const loadInitial = async () => {
        try {
          await fetchOptions('');
        } catch (error) {
          console.warn('Failed to load initial data for Typeahead:', error);
        }
      };
      loadInitial();
    }
  }, [loadInitialData, fetchOptions]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Autocomplete<TypeaheadOption<T>>
      label={label}
      helperText={searchError || helperText}
      error={error || !!searchError}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      value={displayValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      loading={isLoading || loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      size={size}
      sx={sx}
      actions={actions}
      autoFocus={autoFocus}
      inputRef={inputRef}
      placeholder={placeholder}
      clearOnBlur={clearOnBlur}
      clearOnEscape={clearOnEscape}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
      renderOption={renderOption}
      slotProps={slotProps}
    />
  );
};

export default Typeahead;
